import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarLoader } from 'react-spinners';

import DeleteIcon from '@/assets/disable.svg';
import ViewIcon from '@/assets/view.svg';
import ConfirmAnnouncementModal from '@/components/modals/confirm-announcement';
import ConfirmResponseNotificationModal from '@/components/modals/confirm-response-notification';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { StatusBadge } from '@/components/ui/status-badge';
import { useDevicesInCurrentOrganisation } from '@/hooks/use-devices';
import { useOpenState } from '@/hooks/use-open-state';
import { useUnitUrlComposer } from '@/hooks/use-unit-url-composer';
import { useUnits } from '@/hooks/use-units';
import { useUserRole } from '@/hooks/use-user-role';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { SendAnnouncementForm } from '@/pages/authenticated/devices/drawer-views/send-announcement-form';
import { SelectedUnitsActionBar } from '@/pages/authenticated/groups/selected-units-action-bar';
import { api } from '@/redux/api';
import { composeTableOptions } from '@/table';
import { Unit } from '@/types/unit';
import { getOnlineStatus, useOnlineStatusMap } from '@/utils/get-online-status';

type DrawerView = 'announce' | 'responseNotification';
type DrawerContext = { view: DrawerView; units: Unit[] };
type AnnouncementConfirmationContext = {
  units: Unit[];
  notificationText: string;
};
type ResponseNotificationConfirmationContext = {
  units: Unit[];
  notificationText: string;
};
type DeleteUnitConfirmationContext = {
  unit: Unit;
};

export const UnitsPage: FC = () => {
  const navigate = useNavigate();
  const { data: units, isLoading: isLoadingUnits } = useUnits();
  const [deleteUnit] = api.useDeleteUnitMutation();
  const composeUnitUrl = useUnitUrlComposer();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const { role } = useUserRole();

  const { data: devices, isFetching: isFetchingDevices } =
    useDevicesInCurrentOrganisation();
  const onlineStatusMap = useOnlineStatusMap(devices);

  const columns: MRT_ColumnDef<Unit>[] = useMemo(
    () => [
      {
        accessorKey: 'unitName',
        header: 'Unit Name',
        grow: true,
        size: 150,
        Cell: ({ renderedCellValue, row }) => {
          const composeUrl = useUnitUrlComposer();
          return (
            <Link to={composeUrl(row.original.id, row.original.group?.id)}>
              {renderedCellValue}
            </Link>
          );
        },
      },
      {
        id: 'groupName',
        accessorFn: ({ group }) => group?.groupName || 'Unassigned',
        filterVariant: 'multi-select',
        header: 'Group',
        size: 150,
      },
      {
        accessorKey: 'id',
        header: 'Unit Id',
        size: 100,
      },
      {
        id: 'deviceCount',
        accessorFn: ({ devices }) => devices?.length || 0,
        filterVariant: 'range-slider',
        header: 'No. of Devices',
        size: 150,
        Cell: ({ renderedCellValue }) =>
          `${renderedCellValue} ${renderedCellValue === 1 ? 'device' : 'devices'}`,
      },
      {
        id: 'status',
        accessorFn: (row) => getOnlineStatus(onlineStatusMap, row.id),
        filterVariant: 'multi-select',
        header: 'Status',
        grow: false,
        size: 120,
        Cell: ({ row }) => {
          const status = getOnlineStatus(onlineStatusMap, row.original.id);
          if (!status) {
            return isFetchingDevices ? <BarLoader color={'gray'} /> : null;
          }
          return (
            <StatusBadge
              text={status}
              variant={status === 'Online' ? 'success' : 'alert'}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlineStatusMap, isFetchingDevices],
  );

  /**
   * Drawer state for preparing an announcement
   */
  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'announce', units: [] });

  /**
   * Modal state for confirming the announcement
   */
  const {
    state: announcementConfirmationState,
    open: openAnnouncementConfirmation,
    toggleOpen: toggleAnnouncementConfirmationOpen,
  } = useOpenState<AnnouncementConfirmationContext>({
    units: [],
    notificationText: '',
  });

  /**
   * Modal state for confirming the response notification
   */
  const {
    state: responseNotificationConfirmationState,
    open: openResponseNotificationConfirmation,
    toggleOpen: toggleResponseNotificationConfirmationOpen,
  } = useOpenState<ResponseNotificationConfirmationContext>({
    units: [],
    notificationText: '',
  });

  /**
   * Modal state for confirming unit deletion
   */
  const {
    state: deleteUnitConfirmationState,
    open: openDeleteUnitConfirmation,
    toggleOpen: toggleDeleteUnitConfirmationOpen,
  } = useOpenState<DeleteUnitConfirmationContext>({
    unit: {} as Unit,
  });

  /**
   * Extract the unit IDs from the selected rows in the material table
   */
  const selectedUnits = useMemo<Unit[]>(
    () =>
      (Object.keys(rowSelection) as unknown[] as number[]).map(
        (rowIndex) => units[rowIndex],
      ),
    [rowSelection, units],
  );

  const table = useMaterialReactTable({
    ...composeTableOptions<Unit>({
      enableRowActions: true,
      enableRowSelection: true,
    }),
    columns,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    data: units,
    state: {
      isLoading: isLoadingUnits,
      showColumnFilters: true,
      rowSelection,
      ...(isLoadingUnits
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: setRowSelection,
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      row.original.group?.id ? (
        <MRT_ActionMenuItem
          icon={<ViewIcon />}
          key="view"
          label="View in Group"
          onClick={() => {
            closeMenu();
            navigate(composeUnitUrl(row.original.id, row.original.group?.id));
          }}
          table={table}
        />
      ) : (
        <MRT_ActionMenuItem
          disabled
          icon={<ViewIcon />}
          key="view"
          label="Not in group"
          onClick={() => {
            closeMenu();
          }}
          table={table}
        />
      ),
      role?.id !== 3 && (
        <MRT_ActionMenuItem
          icon={<DeleteIcon />}
          key="delete"
          label="Delete"
          onClick={() => {
            closeMenu();
            openDeleteUnitConfirmation({ unit: row.original });
          }}
          table={table}
        />
      ),
    ],
  });

  const getUnitIds = (units: Unit[]): number[] =>
    units.map((unit) => unit.id).filter(Boolean) as number[];

  /**
   * Only one view in this drawer: announcement to units
   */
  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'announce': {
        const { units } = drawerState.context;
        if (units && units.length > 0) {
          return (
            <SendAnnouncementForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openAnnouncementConfirmation({
                  units,
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      case 'responseNotification': {
        const { units } = drawerState.context;
        if (units && units.length > 0) {
          return (
            <SendAnnouncementForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openResponseNotificationConfirmation({
                  units,
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      default:
        break;
    }

    /**
     * If no view has returned anything, close the drawer
     */
    if (drawerState.open) {
      toggleDrawerOpen(false);
    }
    return null;
  };

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePostfix="Management"
          subtitlePrefix="Organisation"
          subtitleTooltip="Unit"
          title="Units"
          tooltip="Units store devices. This could be a household or a room in a ward."
        />
      </HeadingGroupContainerLayout>

      <SelectedUnitsActionBar
        openSendAnnouncement={() =>
          openDrawer({ view: 'announce', units: selectedUnits })
        }
        openSendResponseNotification={() =>
          openDrawer({ view: 'responseNotification', units: selectedUnits })
        }
        selectedUnits={selectedUnits}
      />

      <MaterialReactTable table={table} />

      <Drawer
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
        title="Notification"
      >
        {renderDrawerContent()}
      </Drawer>

      <ConfirmAnnouncementModal
        isOpen={announcementConfirmationState.open}
        notificationText={
          announcementConfirmationState.context.notificationText
        }
        onCancel={() => toggleAnnouncementConfirmationOpen(false)}
        onSend={() => {
          toggleAnnouncementConfirmationOpen(false);
          toggleDrawerOpen(false);
          setRowSelection({});
        }}
        unitIds={getUnitIds(announcementConfirmationState.context.units)}
      />

      <ConfirmResponseNotificationModal
        isOpen={responseNotificationConfirmationState.open}
        notificationText={
          responseNotificationConfirmationState.context.notificationText
        }
        onCancel={() => toggleResponseNotificationConfirmationOpen(false)}
        onSend={() => {
          toggleResponseNotificationConfirmationOpen(false);
          toggleDrawerOpen(false);
          setRowSelection({});
        }}
        unitIds={getUnitIds(
          responseNotificationConfirmationState.context.units,
        )}
      />

      {/* Delete unit confirmation modal */}
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteUnitConfirmationOpen(false)}
            variant="secondary"
          >
            Cancel
          </Button>,
          <Button
            disabled={
              deleteUnitConfirmationState.context.unit?.devices?.length > 0
            }
            key="delete"
            onClick={() => {
              const unit = deleteUnitConfirmationState.context?.unit;
              if (unit?.id && unit?.organisation) {
                deleteUnit({
                  id: unit.id,
                  organisation: unit.organisation,
                });
                toggleDeleteUnitConfirmationOpen(false);
              }
            }}
            variant="primary-warning"
          >
            Delete
          </Button>,
        ]}
        description={
          deleteUnitConfirmationState.context?.unit?.devices?.length > 0
            ? `You cannot delete a unit which has associated devices`
            : `Are you sure you want to delete unit ${deleteUnitConfirmationState.context.unit?.unitName || ''}? This action cannot be undone.`
        }
        open={deleteUnitConfirmationState.open}
        setOpen={toggleDeleteUnitConfirmationOpen}
        title="Delete Unit"
      />
    </>
  );
};
